import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faPencil,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import InlineFieldTooltip from "./InlineFieldTooltip";
import { buildGeneralCostFormulaPreviewText } from "../utils/mobilizationSimulationUtils";
import {
  getTeamDisplayName,
  sortGroupedMobilizationConfigs,
  sortGroupedTeamConfigs,
} from "../utils/mobilizationTeamConfigUtils";

const SPEED_BANDS = ["Slow", "Medium", "Fast"];
const createEmptyMobilizationConfigForm = (year) => ({
  year,
  team_id: "",
  speed_band: "Medium",
  selected_general_cost_ids: [],
  is_active: false,
  sort_order: "99",
  notes: "",
});

const resolveTeamIdFromValue = (value) => {
  const parsed = Number(
    value?.id ?? value?.teamId ?? value?.team_id ?? value ?? NaN,
  );
  return Number.isFinite(parsed) ? parsed : null;
};

const toNum = (value) => Number(value || 0);

function RepairProjectMobilizationConfigPanel({
  selectedConfigId,
  onSelectConfigId,
  onConfigDataChanged,
  refreshKey,
}) {
  const currentYear = new Date().getFullYear();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobilizationConfigYears, setMobilizationConfigYears] = useState([]);
  const [teamYears, setTeamYears] = useState([]);
  const [generalCostYears, setGeneralCostYears] = useState([]);
  const [expandedMobilizationConfigYears, setExpandedMobilizationConfigYears] =
    useState(new Set());

  const [showMobilizationConfigModal, setShowMobilizationConfigModal] =
    useState(false);
  const [savingMobilizationConfig, setSavingMobilizationConfig] =
    useState(false);
  const [editingMobilizationConfigId, setEditingMobilizationConfigId] =
    useState(null);
  const [mobilizationConfigForm, setMobilizationConfigForm] = useState(
    createEmptyMobilizationConfigForm(currentYear),
  );

  const [showCopyMobilizationConfigModal, setShowCopyMobilizationConfigModal] =
    useState(false);
  const [
    copyMobilizationConfigSourceYear,
    setCopyMobilizationConfigSourceYear,
  ] = useState(null);
  const [
    copyMobilizationConfigTargetYear,
    setCopyMobilizationConfigTargetYear,
  ] = useState("");
  const [copyMobilizationConfigError, setCopyMobilizationConfigError] =
    useState("");
  const [copyingMobilizationConfigs, setCopyingMobilizationConfigs] =
    useState(false);

  const fetchPanelData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [mobilizationConfigsRes, teamsRes, generalCostsRes] =
        await Promise.all([
        api.get(
          "/mobilization-master/mobilization-configs?grouped_by_year=true",
        ),
        api.get(
          "/mobilization-master/painter-team-configs?grouped_by_year=true",
        ),
        api.get("/mobilization-master/costs?grouped_by_year=true"),
      ]);

      const teams = sortGroupedTeamConfigs(teamsRes.data?.data || []);
      const teamsByIdMap = new Map();
      teams.forEach((group) => {
        (group.teams || []).forEach((team) => {
          teamsByIdMap.set(Number(team.id), team);
        });
      });
      const configs = sortGroupedMobilizationConfigs(
        mobilizationConfigsRes.data?.data || [],
        teamsByIdMap,
      );
      const generalCosts = generalCostsRes.data?.data || [];

      setMobilizationConfigYears(configs);
      setTeamYears(teams);
      setGeneralCostYears(generalCosts);
      setExpandedMobilizationConfigYears((prev) => {
        const availableYears = new Set(
          (configs || []).map((group) => Number(group.year)),
        );
        const next = new Set(
          Array.from(prev).filter((year) => availableYears.has(Number(year))),
        );

        if (next.size === 0 && configs.length > 0) {
          next.add(Number(configs[0].year));
        }

        return next;
      });
    } catch (fetchError) {
      console.error(
        "Error fetching mobilization config panel data:",
        fetchError,
      );
      setError("Failed to load teams and productivity settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPanelData();
  }, [fetchPanelData, refreshKey]);

  useEffect(() => {
    if (!selectedConfigId) return;

    const selectedGroup = (mobilizationConfigYears || []).find((group) =>
      (group.configs || []).some(
        (row) => Number(row.id) === Number(selectedConfigId),
      ),
    );

    if (!selectedGroup) return;

    setExpandedMobilizationConfigYears((prev) => {
      const next = new Set(prev);
      next.add(Number(selectedGroup.year));
      return next;
    });
  }, [mobilizationConfigYears, selectedConfigId]);

  const allYears = useMemo(() => {
    const years = new Set([currentYear]);
    teamYears.forEach((group) => years.add(Number(group.year)));
    generalCostYears.forEach((group) => years.add(Number(group.year)));
    mobilizationConfigYears.forEach((group) => years.add(Number(group.year)));
    return Array.from(years).sort((left, right) => right - left);
  }, [currentYear, generalCostYears, mobilizationConfigYears, teamYears]);

  const mobilizationConfigAllYears = useMemo(() => {
    const years = new Set([currentYear]);
    mobilizationConfigYears.forEach((group) => years.add(Number(group.year)));
    return Array.from(years).sort((left, right) => right - left);
  }, [currentYear, mobilizationConfigYears]);

  const modalYearOptions = useMemo(() => {
    const years = new Set(allYears);
    years.add(currentYear + 1);
    return Array.from(years).sort((left, right) => right - left);
  }, [allYears, currentYear]);

  const teamsById = useMemo(() => {
    const map = new Map();
    teamYears.forEach((group) => {
      (group.teams || []).forEach((team) => {
        map.set(Number(team.id), team);
      });
    });
    return map;
  }, [teamYears]);

  const resolveTeamDisplayName = useCallback(
    (teamLike, fallback = "-") => {
      const teamId = resolveTeamIdFromValue(teamLike);
      if (teamId !== null && teamsById.has(teamId)) {
        return getTeamDisplayName(teamsById.get(teamId)) || fallback;
      }

      return getTeamDisplayName(teamLike) || fallback;
    },
    [teamsById],
  );

  const resolveEligibleTeamsForYear = useCallback(
    (year) => {
      const targetYear = Number(year || currentYear);
      return (teamYears || [])
        .filter((group) => Number(group.year) <= targetYear)
        .sort((left, right) => Number(right.year) - Number(left.year))
        .flatMap((group) =>
          (group.teams || []).map((team) => ({
            id: Number(team.id),
            year: Number(group.year),
            label: `${getTeamDisplayName(team)} (${group.year})`,
          })),
        );
    },
    [currentYear, teamYears],
  );

  const resolveGeneralCostsForMobilizationYear = useCallback(
    (year) => {
      const targetYear = Number(year || currentYear);
      const sourceGroup = (generalCostYears || [])
        .filter((group) => Number(group.year) <= targetYear)
        .sort((left, right) => Number(right.year) - Number(left.year))[0];

      return {
        sourceYear: sourceGroup ? Number(sourceGroup.year) : null,
        costs: sourceGroup?.costs || [],
      };
    },
    [currentYear, generalCostYears],
  );

  const defaultGeneralCostIdsForYear = useCallback(
    (year) =>
      resolveGeneralCostsForMobilizationYear(year)
        .costs.map((row) => Number(row.id))
        .filter((id) => Number.isInteger(id) && id > 0),
    [resolveGeneralCostsForMobilizationYear],
  );

  const eligibleTeamsForMobilizationForm = useMemo(
    () => resolveEligibleTeamsForYear(mobilizationConfigForm.year),
    [mobilizationConfigForm.year, resolveEligibleTeamsForYear],
  );

  const generalCostsForMobilizationForm = useMemo(
    () => resolveGeneralCostsForMobilizationYear(mobilizationConfigForm.year),
    [mobilizationConfigForm.year, resolveGeneralCostsForMobilizationYear],
  );

  const resetMobilizationConfigForm = useCallback(() => {
    setEditingMobilizationConfigId(null);
    setMobilizationConfigForm({
      ...createEmptyMobilizationConfigForm(currentYear),
      selected_general_cost_ids: defaultGeneralCostIdsForYear(currentYear),
    });
  }, [currentYear, defaultGeneralCostIdsForYear]);

  const openCreateMobilizationConfigModal = () => {
    resetMobilizationConfigForm();
    setShowMobilizationConfigModal(true);
  };

  const openEditMobilizationConfigModal = (row, year) => {
    setEditingMobilizationConfigId(Number(row.id));
    setMobilizationConfigForm({
      year: Number(year),
      team_id: String(row.teamId || row.team_id || row.teamConfig?.id || ""),
      speed_band: String(row.speedBand || row.speed_band || "Medium"),
      selected_general_cost_ids:
        (row.selectedGeneralCostIds || row.selected_general_cost_ids || [])
          .length > 0
          ? (row.selectedGeneralCostIds || row.selected_general_cost_ids || [])
              .map((id) => Number(id))
              .filter((id) => Number.isInteger(id) && id > 0)
          : defaultGeneralCostIdsForYear(year),
      is_active: Boolean(row.isActive || row.is_active),
      sort_order: String(toNum(row.sortOrder || row.sort_order || 99)),
      notes: String(row.notes || ""),
    });
    setShowMobilizationConfigModal(true);
  };

  const closeMobilizationConfigModal = () => {
    setShowMobilizationConfigModal(false);
    resetMobilizationConfigForm();
  };

  const notifyConfigDataChanged = useCallback(() => {
    if (typeof onConfigDataChanged === "function") {
      onConfigDataChanged();
    }
  }, [onConfigDataChanged]);

  const handleSaveMobilizationConfig = async () => {
    if (!mobilizationConfigForm.team_id) {
      alert("Please select a team.");
      return;
    }

    setSavingMobilizationConfig(true);

    try {
      const payload = {
        year: Number(mobilizationConfigForm.year),
        team_id: Number(mobilizationConfigForm.team_id),
        speed_band: mobilizationConfigForm.speed_band,
        selected_general_cost_ids: (
          mobilizationConfigForm.selected_general_cost_ids || []
        )
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0),
        is_active: Boolean(mobilizationConfigForm.is_active),
        sort_order: toNum(mobilizationConfigForm.sort_order),
        notes: mobilizationConfigForm.notes?.trim() || null,
      };

      const response = editingMobilizationConfigId
        ? await api.put(
            `/mobilization-master/mobilization-configs/${editingMobilizationConfigId}`,
            payload,
          )
        : await api.post("/mobilization-master/mobilization-configs", payload);

      const savedId =
        Number(response.data?.data?.id || editingMobilizationConfigId || 0) ||
        null;

      closeMobilizationConfigModal();
      await fetchPanelData();
      if (savedId && typeof onSelectConfigId === "function") {
        onSelectConfigId(savedId);
      }
      notifyConfigDataChanged();
    } catch (saveError) {
      alert(
        saveError.response?.data?.message ||
          "Failed to save mobilization cost.",
      );
    } finally {
      setSavingMobilizationConfig(false);
    }
  };

  const handleDeleteMobilizationConfig = async (configId) => {
    if (!window.confirm("Delete this mobilization cost row?")) {
      return;
    }

    try {
      await api.delete(`/mobilization-master/mobilization-configs/${configId}`);
      await fetchPanelData();
      if (
        Number(configId) === Number(selectedConfigId) &&
        typeof onSelectConfigId === "function"
      ) {
        onSelectConfigId(null);
      }
      notifyConfigDataChanged();
    } catch (deleteError) {
      alert(
        deleteError.response?.data?.message ||
          "Failed to delete mobilization cost.",
      );
    }
  };

  const handleDeleteMobilizationConfigsYear = async (year) => {
    const group = mobilizationConfigYears.find(
      (g) => Number(g.year) === Number(year),
    );
    const configs = (group && group.configs) || [];
    if (!configs.length) {
      alert(`No mobilization cost rows found for ${year}.`);
      return;
    }
    if (
      !window.confirm(
        `Delete all ${configs.length} mobilization cost row(s) for ${year}? This cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      await Promise.all(
        configs.map((row) =>
          api.delete(`/mobilization-master/mobilization-configs/${row.id}`),
        ),
      );
      await fetchPanelData();
      if (
        configs.some((row) => Number(row.id) === Number(selectedConfigId)) &&
        typeof onSelectConfigId === "function"
      ) {
        onSelectConfigId(null);
      }
      notifyConfigDataChanged();
    } catch (deleteError) {
      alert(
        deleteError.response?.data?.message ||
          "Failed to delete mobilization costs for year.",
      );
    }
  };

  const handleToggleMobilizationConfigActive = async (row, checked) => {
    try {
      await api.put(`/mobilization-master/mobilization-configs/${row.id}`, {
        is_active: checked,
      });
      await fetchPanelData();
      notifyConfigDataChanged();
    } catch (toggleError) {
      alert(
        toggleError.response?.data?.message || "Failed to update active state.",
      );
    }
  };

  const handleCopyMobilizationConfigsFromYear = (sourceYear) => {
    const source = mobilizationConfigYears.find(
      (group) => Number(group.year) === Number(sourceYear),
    );

    if (!source || !(source.configs || []).length) {
      alert(`No mobilization costs found in source year ${sourceYear}.`);
      return;
    }

    const usedYears = new Set(
      mobilizationConfigAllYears.map((year) => Number(year)),
    );
    let suggestedYear = Number(sourceYear) + 1;
    while (usedYears.has(suggestedYear) && suggestedYear <= 3000) {
      suggestedYear += 1;
    }

    setCopyMobilizationConfigSourceYear(Number(sourceYear));
    setCopyMobilizationConfigTargetYear(String(suggestedYear));
    setCopyMobilizationConfigError("");
    setShowCopyMobilizationConfigModal(true);
  };

  const closeCopyMobilizationConfigModal = () => {
    if (copyingMobilizationConfigs) {
      return;
    }

    setShowCopyMobilizationConfigModal(false);
    setCopyMobilizationConfigSourceYear(null);
    setCopyMobilizationConfigTargetYear("");
    setCopyMobilizationConfigError("");
  };

  const handleConfirmCopyMobilizationConfigs = async () => {
    if (!copyMobilizationConfigSourceYear) {
      return;
    }

    const targetYear = parseInt(copyMobilizationConfigTargetYear, 10);
    if (
      !Number.isInteger(targetYear) ||
      targetYear < 1900 ||
      targetYear > 3000
    ) {
      setCopyMobilizationConfigError("Please enter a valid target year.");
      return;
    }

    if (targetYear === copyMobilizationConfigSourceYear) {
      setCopyMobilizationConfigError(
        "Target year must be different from source year.",
      );
      return;
    }

    if (mobilizationConfigAllYears.includes(targetYear)) {
      setCopyMobilizationConfigError(
        `Year ${targetYear} already exists. Please choose a new year.`,
      );
      return;
    }

    const source = mobilizationConfigYears.find(
      (group) =>
        Number(group.year) === Number(copyMobilizationConfigSourceYear),
    );
    if (!source || !(source.configs || []).length) {
      setCopyMobilizationConfigError(
        `No mobilization costs found in source year ${copyMobilizationConfigSourceYear}.`,
      );
      return;
    }

    setCopyingMobilizationConfigs(true);

    try {
      const results = await Promise.allSettled(
        (source.configs || []).map((row) =>
          api.post("/mobilization-master/mobilization-configs", {
            year: targetYear,
            team_id: Number(
              row.teamId || row.team_id || row.teamConfig?.id || 0,
            ),
            speed_band: String(row.speedBand || row.speed_band || "Medium"),
            selected_general_cost_ids: (
              row.selectedGeneralCostIds ||
              row.selected_general_cost_ids ||
              []
            )
              .map((id) => Number(id))
              .filter((id) => Number.isInteger(id) && id > 0),
            is_active: Boolean(row.isActive || row.is_active),
            sort_order: toNum(row.sortOrder || row.sort_order || 99),
            notes: row.notes || null,
          }),
        ),
      );

      const successful = results.filter(
        (result) => result.status === "fulfilled",
      ).length;
      const failed = results.length - successful;

      if (failed > 0) {
        alert(
          `Copied ${successful} of ${results.length} mobilization costs. ${failed} failed.`,
        );
      } else {
        alert(
          `Successfully copied ${successful} mobilization costs from ${copyMobilizationConfigSourceYear} to ${targetYear}.`,
        );
      }

      setShowCopyMobilizationConfigModal(false);
      setCopyMobilizationConfigSourceYear(null);
      setCopyMobilizationConfigTargetYear("");
      setCopyMobilizationConfigError("");
      await fetchPanelData();
      notifyConfigDataChanged();
    } catch (copyError) {
      setCopyMobilizationConfigError(
        copyError.response?.data?.message ||
          "Failed to copy mobilization costs.",
      );
    } finally {
      setCopyingMobilizationConfigs(false);
    }
  };

  const toggleMobilizationConfigYear = (year) => {
    setExpandedMobilizationConfigYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) {
        next.delete(year);
      } else {
        next.add(year);
      }
      return next;
    });
  };

  return (
    <>
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3 pb-4">
          <h3 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
            <span>Teams & Productivity Settings</span>
            <InlineFieldTooltip
              page="mobilization_costs_management"
              section="mobilization_costs"
              fieldKey="mobilization_costs"
            />
          </h3>
          <button
            type="button"
            onClick={openCreateMobilizationConfigModal}
            className="rounded-lg bg-slate-700 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Add New Configuration
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border border-slate-200">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="w-8 px-2.5 py-2 text-left"></th>
                <th className="w-16 px-2.5 py-2 text-left">Year</th>
                <th className="px-2.5 py-2 text-left">Details</th>
                <th className="w-16 px-2.5 py-2 text-left">Active</th>
                <th className="w-28 px-2.5 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-6 text-center text-slate-500"
                  >
                    Loading teams and productivity settings...
                  </td>
                </tr>
              )}

              {!loading && mobilizationConfigYears.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-6 text-center text-slate-500"
                  >
                    No mobilization costs yet.
                  </td>
                </tr>
              )}

              {!loading &&
                mobilizationConfigYears.map((group) => {
                  const isExpanded = expandedMobilizationConfigYears.has(
                    Number(group.year),
                  );

                  return (
                    <React.Fragment
                      key={`repair-project-mobilization-year-${group.year}`}
                    >
                      <tr className="bg-slate-50 hover:bg-slate-100">
                        <td className="px-2.5 py-2">
                          <button
                            type="button"
                            onClick={() =>
                              toggleMobilizationConfigYear(Number(group.year))
                            }
                            className="text-slate-500 hover:text-slate-700"
                            title={isExpanded ? "Collapse year" : "Expand year"}
                          >
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronDown : faChevronRight}
                            />
                          </button>
                        </td>
                        <td className="px-2.5 py-2 font-semibold text-slate-900">
                          {group.year}
                        </td>
                        <td colSpan={2} className="px-2.5 py-2 text-slate-600">
                          {(group.configs || []).length} mobilization cost rows
                        </td>
                        <td className="px-2.5 py-2">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                handleCopyMobilizationConfigsFromYear(group.year)
                              }
                              className="inline-flex items-center rounded-md border border-indigo-300 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                            >
                              Copy
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteMobilizationConfigsYear(group.year)
                              }
                              className="text-red-600 hover:text-red-900"
                              title={`Delete all mobilization cost rows for ${group.year}`}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded &&
                        (group.configs || []).map((row) => {
                          const teamName = resolveTeamDisplayName(
                            row.teamConfig || row.teamId || row.team_id,
                            "-",
                          );
                          const isSelected =
                            Number(row.id) === Number(selectedConfigId);

                          return (
                            <tr
                              key={`repair-project-mobilization-row-${row.id}`}
                              className={
                                isSelected
                                  ? "cursor-pointer bg-blue-50"
                                  : "cursor-pointer bg-white hover:bg-slate-50"
                              }
                              onClick={() =>
                                onSelectConfigId?.(Number(row.id) || null)
                              }
                              title="Select row to recalculate estimate"
                            >
                              <td className="px-2.5 py-2"></td>
                              <td className="px-2.5 py-2 text-slate-700">
                                {row.year}
                              </td>
                              <td className="px-2.5 py-2 break-words text-slate-900">
                                <div className="font-medium leading-5">
                                  {teamName}
                                </div>
                                <div className="mt-0.5 text-xs leading-5 text-slate-600">
                                  {`${row.speedBand || row.speed_band || "-"}`}
                                </div>
                              </td>
                              <td className="px-2.5 py-2 text-slate-700">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleToggleMobilizationConfigActive(
                                      row,
                                      !Boolean(row.isActive || row.is_active),
                                    );
                                  }}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                    Boolean(row.isActive || row.is_active)
                                      ? "bg-blue-600"
                                      : "bg-gray-300"
                                  }`}
                                  title={
                                    Boolean(row.isActive || row.is_active)
                                      ? "Deactivate"
                                      : "Activate"
                                  }
                                  aria-label={`Toggle active for row ${row.id}`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                      Boolean(row.isActive || row.is_active)
                                        ? "translate-x-6"
                                        : "translate-x-1"
                                    }`}
                                  />
                                </button>
                              </td>
                              <td className="whitespace-nowrap px-2.5 py-2">
                                <button
                                  type="button"
                                  className="text-blue-600 hover:text-blue-900"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openEditMobilizationConfigModal(
                                      row,
                                      group.year,
                                    );
                                  }}
                                  title="Edit row"
                                >
                                  <FontAwesomeIcon
                                    icon={faPencil}
                                    className="h-4 w-4"
                                  />
                                </button>
                                <button
                                  type="button"
                                  className="ml-2 text-red-600 hover:text-red-900"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDeleteMobilizationConfig(row.id);
                                  }}
                                  title="Delete row"
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="h-4 w-4"
                                  />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {showMobilizationConfigModal && (
        <div className="fixed inset-0 z-[90] overflow-y-auto bg-gray-600 bg-opacity-50 p-4">
          <div className="relative mx-auto mt-10 w-full max-w-4xl rounded border bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <span>
                  {editingMobilizationConfigId
                    ? "Edit Mobilization Cost"
                    : "Add New Configuration"}
                </span>
                <InlineFieldTooltip
                  page="mobilization_costs"
                  section="modal_titles"
                  fieldKey="mobilization_cost_form"
                  fallbackText="Create or edit mobilization cost simulation inputs."
                  title="Mobilization Cost Form"
                />
              </h3>
              <button
                type="button"
                onClick={closeMobilizationConfigModal}
                className="text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm text-gray-700">Year</label>
                <select
                  value={mobilizationConfigForm.year}
                  onChange={(event) =>
                    setMobilizationConfigForm((prev) => {
                      const nextYear = Number(event.target.value);
                      return {
                        ...prev,
                        year: nextYear,
                        team_id: "",
                        selected_general_cost_ids:
                          defaultGeneralCostIdsForYear(nextYear),
                      };
                    })
                  }
                  className="w-full rounded border border-gray-300 px-3 py-2"
                >
                  {modalYearOptions.map((year) => (
                    <option
                      key={`repair-project-mobilization-year-${year}`}
                      value={year}
                    >
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700">Team</label>
                <select
                  value={mobilizationConfigForm.team_id}
                  onChange={(event) =>
                    setMobilizationConfigForm((prev) => ({
                      ...prev,
                      team_id: event.target.value,
                    }))
                  }
                  className="w-full rounded border border-gray-300 px-3 py-2"
                >
                  <option value="">Select Team</option>
                  {eligibleTeamsForMobilizationForm.map((team) => (
                    <option
                      key={`repair-project-mobilization-team-${team.id}`}
                      value={team.id}
                    >
                      {team.label}
                    </option>
                  ))}
                </select>
                {eligibleTeamsForMobilizationForm.length === 0 && (
                  <p className="mt-1 text-xs text-red-600">
                    No teams found for selected year or previous years.
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  Paint Speed
                </label>
                <select
                  value={mobilizationConfigForm.speed_band}
                  onChange={(event) =>
                    setMobilizationConfigForm((prev) => ({
                      ...prev,
                      speed_band: event.target.value,
                    }))
                  }
                  className="w-full rounded border border-gray-300 px-3 py-2"
                >
                  {SPEED_BANDS.map((band) => (
                    <option
                      key={`repair-project-mobilization-speed-${band}`}
                      value={band}
                    >
                      {band}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  Sort Order
                </label>
                <input
                  type="number"
                  step="1"
                  value={mobilizationConfigForm.sort_order}
                  onChange={(event) =>
                    setMobilizationConfigForm((prev) => ({
                      ...prev,
                      sort_order: event.target.value,
                    }))
                  }
                  className="w-full rounded border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  Active for Year
                </label>
                <div className="mt-2 inline-flex items-center gap-3 text-sm text-gray-700">
                  <button
                    type="button"
                    onClick={() =>
                      setMobilizationConfigForm((prev) => ({
                        ...prev,
                        is_active: !Boolean(prev.is_active),
                      }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      Boolean(mobilizationConfigForm.is_active)
                        ? "bg-blue-600"
                        : "bg-gray-300"
                    }`}
                    title={
                      Boolean(mobilizationConfigForm.is_active)
                        ? "Deactivate"
                        : "Activate"
                    }
                    aria-label="Toggle active for selected year"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        Boolean(mobilizationConfigForm.is_active)
                          ? "translate-x-6"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                  <span>Set this row as active for selected year</span>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm text-gray-700">
                  General Costs Selection
                </label>
                <div className="rounded border border-gray-300 p-3">
                  <div className="mb-3 text-xs text-gray-600">
                    {generalCostsForMobilizationForm.sourceYear
                      ? `Using General Costs from year ${generalCostsForMobilizationForm.sourceYear} (same or nearest previous year).`
                      : "No General Costs found for selected year or previous years."}
                  </div>

                  {generalCostsForMobilizationForm.costs.length > 0 ? (
                    <div className="max-h-52 space-y-2 overflow-y-auto pr-1">
                      {generalCostsForMobilizationForm.costs.map((cost) => {
                        const costId = Number(cost.id);
                        const selected = (
                          mobilizationConfigForm.selected_general_cost_ids || []
                        ).includes(costId);

                        return (
                          <div
                            key={`repair-project-mobilization-general-cost-${cost.id}`}
                            className="flex items-center justify-between rounded border border-gray-200 px-3 py-2"
                          >
                            <div className="min-w-0">
                              <div className="truncate text-sm text-gray-900">
                                {cost.costCategory || cost.cost_category}
                              </div>
                              <div className="truncate text-xs text-gray-500">
                                {buildGeneralCostFormulaPreviewText(cost)}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setMobilizationConfigForm((prev) => {
                                  const next = new Set(
                                    prev.selected_general_cost_ids || [],
                                  );
                                  if (selected) {
                                    next.delete(costId);
                                  } else {
                                    next.add(costId);
                                  }

                                  return {
                                    ...prev,
                                    selected_general_cost_ids: Array.from(next),
                                  };
                                })
                              }
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                selected ? "bg-blue-600" : "bg-gray-300"
                              }`}
                              title={selected ? "Deselect" : "Select"}
                              aria-label={`Toggle general cost ${costId}`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  selected ? "translate-x-6" : "translate-x-1"
                                }`}
                              />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      Add General Costs first to enable selection.
                    </div>
                  )}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="mb-1 block text-sm text-gray-700">
                  Notes
                </label>
                <textarea
                  value={mobilizationConfigForm.notes}
                  onChange={(event) =>
                    setMobilizationConfigForm((prev) => ({
                      ...prev,
                      notes: event.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full rounded border border-gray-300 px-3 py-2"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button
                type="button"
                onClick={closeMobilizationConfigModal}
                className="rounded border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveMobilizationConfig}
                disabled={
                  savingMobilizationConfig ||
                  eligibleTeamsForMobilizationForm.length === 0
                }
                className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {savingMobilizationConfig
                  ? "Saving..."
                  : editingMobilizationConfigId
                    ? "Update Cost"
                    : "Save Cost"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCopyMobilizationConfigModal && (
        <div className="fixed inset-0 z-50 flex h-full w-full items-center justify-center overflow-y-auto bg-gray-600 bg-opacity-50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl">
            <h3 className="mb-2 flex items-center gap-2 text-lg font-medium text-gray-900">
              <span>Copy To Year</span>
              <InlineFieldTooltip
                page="mobilization_costs"
                section="modal_titles"
                fieldKey="copy_mobilization_costs_to_year"
                fallbackText="Copy all mobilization cost rows from one year to another."
                title="Copy To Year"
              />
            </h3>
            <p className="mb-4 text-sm text-gray-600">
              Copy all mobilization costs from
              <strong> {copyMobilizationConfigSourceYear}</strong> to a new
              year.
            </p>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Target Year
            </label>
            <input
              type="number"
              value={copyMobilizationConfigTargetYear}
              onChange={(event) => {
                setCopyMobilizationConfigTargetYear(event.target.value);
                setCopyMobilizationConfigError("");
              }}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. 2027"
            />
            {copyMobilizationConfigError && (
              <p className="mt-2 text-sm text-red-600">
                {copyMobilizationConfigError}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCopyMobilizationConfigModal}
                className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={copyingMobilizationConfigs}
                onClick={handleConfirmCopyMobilizationConfigs}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {copyingMobilizationConfigs ? "Copying..." : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RepairProjectMobilizationConfigPanel;
